/**
 * CRM v2 wire shapes and mappers for the mailbox-health and rates routes
 * (contract §2 and §4), split out of outreachWire.ts (which re-exports them).
 * Both routes are new, so every field is read defensively: a value that is
 * missing or of the wrong type maps to null (or drops the malformed item)
 * rather than failing the whole card.
 */
import type {
  OutreachMailboxHealth,
  OutreachMailboxHealthItem,
  OutreachMoney,
  OutreachRateFormat,
  OutreachRatePackage,
  OutreachRatesPage,
  OutreachRatesRow,
} from '@discovr/types';
import { isOutreachBrandState, isOutreachMailboxLimit, isOutreachMailboxStatus } from '@discovr/types';
import { countOrNull, countOrZero, isRecord, numberOrNull, textOrNull } from './outreachWireValues';

export interface WireMailboxHealthItem {
  id: number;
  address: string;
  domain?: string;
  status?: string;
  paused_reason?: string;
  sent_today?: number;
  cap_today?: number;
  sends_7d?: number;
  hard_bounces_7d?: number;
  bounce_rate_7d?: number | null;
  bounce_limit?: number;
  min_sends_for_bounce?: number;
  insufficient_volume?: boolean;
  domain_spam_rate?: number | null;
  spam_warn?: number;
  headroom?: number;
  tightest?: string;
}

export interface WireMailboxHealth {
  source?: string;
  generated_at?: string;
  mailboxes?: WireMailboxHealthItem[] | null;
}

export interface WireMoney {
  amount: number;
  currency: string;
}

export interface WireRateFormat {
  format: string;
  quoted?: WireMoney | null;
  locked?: WireMoney | null;
  quoted_at?: string | null;
  quantity?: number;
}

export interface WireRatePackage {
  deliverable_ids?: string[] | null;
  quantity?: number;
  amount?: number;
  currency?: string;
  quoted_at?: string | null;
}

export interface WireRatesRow {
  entry_id: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
  status?: string;
  outreach_state?: string;
  brand_state?: string;
  formats?: WireRateFormat[] | null;
  packages?: WireRatePackage[] | null;
  unassigned_quotes?: WireMoney[] | null;
  revisions?: number;
  locked_by?: string | null;
  locked_at?: string | null;
  quote_after_lock?: boolean;
}

export interface WireRatesPage {
  total?: number;
  rows?: WireRatesRow[] | null;
}

const MIN_HEADROOM = 0;
const MAX_HEADROOM = 1;

const clampHeadroom = (value: number | null): number | null =>
  value === null ? null : Math.min(MAX_HEADROOM, Math.max(MIN_HEADROOM, value));

/** A mailbox without a numeric id cannot be picked, so it is dropped. One Go reports by id alone (no longer in the
 *  pool, as disabled) keeps a null address and is named by the UI. */
function toMailboxHealthItem(w: unknown): OutreachMailboxHealthItem | null {
  if (!isRecord(w)) return null;
  const id = numberOrNull(w.id);
  if (id === null) return null;
  return {
    id,
    address: textOrNull(w.address),
    domain: textOrNull(w.domain) ?? '',
    status: isOutreachMailboxStatus(w.status) ? w.status : null,
    pausedReason: textOrNull(w.paused_reason),
    sentToday: countOrZero(w.sent_today),
    capToday: countOrZero(w.cap_today),
    sends7d: countOrZero(w.sends_7d),
    hardBounces7d: countOrZero(w.hard_bounces_7d),
    bounceRate7d: numberOrNull(w.bounce_rate_7d),
    bounceLimit: numberOrNull(w.bounce_limit),
    minSendsForBounce: countOrNull(w.min_sends_for_bounce),
    insufficientVolume: w.insufficient_volume === true,
    domainSpamRate: numberOrNull(w.domain_spam_rate),
    spamWarn: numberOrNull(w.spam_warn),
    headroom: clampHeadroom(numberOrNull(w.headroom)),
    tightest: isOutreachMailboxLimit(w.tightest) ? w.tightest : null,
  };
}

const isPresent = <T>(value: T | null): value is T => value !== null;

/** Every item that maps, in Go's order; anything that is not a list reads as empty. */
function listOf<T>(value: unknown, map: (item: unknown) => T | null): T[] {
  return Array.isArray(value) ? value.map(map).filter(isPresent) : [];
}

export function toMailboxHealth(w: unknown): OutreachMailboxHealth {
  const body = isRecord(w) ? w : {};
  return {
    source: body.source === 'pool' ? 'pool' : 'campaign',
    generatedAt: textOrNull(body.generated_at),
    mailboxes: listOf(body.mailboxes, toMailboxHealthItem),
  };
}

/** Go sends an empty currency when neither a confirm nor a quote names one; the amount is still real, so it is kept. */
function toMoney(w: unknown): OutreachMoney | null {
  if (!isRecord(w)) return null;
  const amount = numberOrNull(w.amount);
  if (amount === null) return null;
  return { amount, currency: textOrNull(w.currency) ?? '' };
}

function toRateFormat(w: unknown): OutreachRateFormat | null {
  if (!isRecord(w)) return null;
  const format = textOrNull(w.format);
  if (format === null) return null;
  return { format, quoted: toMoney(w.quoted), locked: toMoney(w.locked), quotedAt: textOrNull(w.quoted_at), quantity: quantityOrOne(w.quantity) };
}

/** Go sends quantity 1 for now; an older Go sends none, which also reads as 1. */
function quantityOrOne(v: unknown): number {
  const n = countOrNull(v);
  return n !== null && n >= 1 ? n : 1;
}

/** A package with no deliverable, no amount or no time cannot be worded, so it is dropped. */
function toRatePackage(w: unknown): OutreachRatePackage | null {
  if (!isRecord(w)) return null;
  const deliverableIds = listOf(w.deliverable_ids, textOrNull);
  const price = toMoney({ amount: w.amount, currency: w.currency });
  const quotedAt = textOrNull(w.quoted_at);
  if (deliverableIds.length === 0 || price === null || quotedAt === null) return null;
  return { deliverableIds, quantity: quantityOrOne(w.quantity), price, quotedAt };
}

/** A row without an entry id or a username cannot be shown or opened, so it is dropped. */
function toRatesRow(w: unknown): OutreachRatesRow | null {
  if (!isRecord(w)) return null;
  const entryId = textOrNull(w.entry_id);
  const username = textOrNull(w.username);
  if (entryId === null || username === null) return null;
  return {
    entryId,
    username,
    displayName: textOrNull(w.display_name) ?? undefined,
    avatarUrl: textOrNull(w.avatar_url) ?? undefined,
    brandState: isOutreachBrandState(w.brand_state) ? w.brand_state : 'none',
    formats: listOf(w.formats, toRateFormat),
    packages: listOf(w.packages, toRatePackage),
    unassignedQuotes: listOf(w.unassigned_quotes, toMoney),
    revisions: countOrZero(w.revisions),
    lockedBy: textOrNull(w.locked_by),
    lockedAt: textOrNull(w.locked_at),
    quoteAfterLock: w.quote_after_lock === true,
  };
}

export function toRatesPage(w: unknown): OutreachRatesPage {
  const body = isRecord(w) ? w : {};
  const rows = listOf(body.rows, toRatesRow);
  return { rows, total: countOrNull(body.total) ?? rows.length };
}
